/**
 * The share register as a spreadsheet, and back — decisions 56 and 83.
 *
 * The intended flow is export → mass-edit in Excel → upload, which is why the
 * import **replaces the whole register** rather than merging. Merging would
 * make a deleted row indistinguishable from a row somebody forgot to include,
 * and there is no safe guess between those two.
 *
 * ## Every field, because the export is the backup — decision 83
 *
 * Because the import replaces the register, any field the file omits is
 * *deleted from a company's share register* by somebody who thought they were
 * fixing a typo in one row. That is not theoretical. Between 18 and 24 Aug 2026
 * this file carried sixteen columns while the record carried thirty-odd, so a
 * legitimate export → import silently destroyed the thirteen aksjeeierbok
 * fields, every subscription against the open round, and the agreed prices on
 * the kr 461,22 and kr 307,47 holdings.
 *
 * So the rule is decision 83's, and it is absolute: **every field on
 * `Shareholder` has a column here, and a field added to the record gets its
 * column in the same commit.** `scripts/checkShareholderCsv.ts` builds its
 * fixture from `EMPTY_SHAREHOLDER` plus every optional field precisely so that
 * adding a field fails the guard until this file carries it.
 *
 * ## Semicolons, and a BOM
 *
 * A Norwegian Excel splits on semicolons and treats a comma as a decimal
 * separator, so a comma-delimited file opens as one mangled column and every
 * `kr 9,50` lands in the wrong place. The BOM is what tells it the file is
 * UTF-8; without it `Bjarte Olsen` survives and `Kjærsgård` does not.
 *
 * ## Lossless means ids, not names
 *
 * Decision 56 asks for a lossless round trip and decision 48 keys every link on
 * an account id rather than a name. Those two together decide the shape here:
 * the export carries the ids, so a re-import restores the same links, **and**
 * carries `linkedLabel` beside them so the file is legible to a person reading
 * it in Excel. The label is a display copy — a name typed into a spreadsheet is
 * never resolved back to an account, because silently matching on it is how a
 * shareholder ends up linked to the wrong lead.
 *
 * ## Personnummer is in the file
 *
 * Decision 14 keeps it out of every list view; decision 56 puts it in the
 * export. Those are not in tension: a file an admin deliberately downloads is
 * not a screen anybody wanders onto, and an export that dropped it would not be
 * a backup of the register — it would be a lossy copy that quietly discards the
 * one field the aksjeeierbok legally turns on.
 */
import type {
  Attachment,
  OptionGrant,
  ShareholderMatches,
  SharePurchase,
  Shareholder,
  Subscription,
} from '@/types/ownership'
import type { GeoPoint } from '@/types/geo'

/**
 * Every column the register holds, in the order the export writes them.
 *
 * Grouped the way the aksjeeierbok itself reads — who they are, how to reach
 * them, what the register records about the holding, where else they appear in
 * Skida, then the ledgers — rather than in the order the fields happened to be
 * added to the type.
 *
 * Import reads **by header name, not by position**, so a person who reorders
 * columns in Excel — or deletes one they do not care about — still gets a file
 * that imports, and a file exported before this order changed still reads. A
 * column that is absent is reported as one that will be cleared, rather than
 * shifting every value one place to the left.
 */
export const CSV_COLUMNS = [
  // Who they are
  'id',
  'name',
  'legalName',
  'kind',
  'orgNumber',
  'personnummer',
  'birthNote',
  'contactPerson',
  // How to reach them
  'email',
  'altEmail',
  'phone',
  'address',
  'postalCode',
  'city',
  'country',
  // What the register records about the holding itself
  'entryDate',
  'shareSeries',
  'vpsAccount',
  'folkeinvestRef',
  'nameVariants',
  'sources',
  // Also in the CRM — decision 48
  'linkedLeadId',
  'linkedLeadContactId',
  'linkedLeadContact',
  'linkedAmbassadorId',
  'linkedTeamEmail',
  'linkedLabel',
  // The three ledgers. Interest counts towards nothing — decision 79.
  'purchases',
  'subscriptions',
  'options',
  // Written by a script, never typed — see `MACHINE_WRITTEN`
  'geo',
  'matches',
  'attachments',
  'createdAt',
  'updatedAt',
] as const

/**
 * Columns no person fills in, and whose absence from a file loses nothing.
 *
 * `geo` comes from `scripts/geocodeAll.ts`, `matches` from
 * `scripts/matchShareholders.ts`, `attachments` from Storage, and the two
 * timestamps from the write itself. They are written to the file so that it is
 * a backup rather than most of one — a register restored onto an empty
 * collection has to come back whole — but on import the record wins wherever
 * one exists, so an edit in Excel can never corrupt a Storage path or invent a
 * coordinate. They are also left out of the "these columns will be cleared"
 * warning, because nothing is cleared when they are missing.
 */
const MACHINE_WRITTEN: readonly string[] = [
  'geo',
  'matches',
  'attachments',
  'createdAt',
  'updatedAt',
]

const SEP = ';'

/** Quotes are doubled inside a quoted field, as every spreadsheet expects. */
function escape(value: unknown): string {
  const s = value === null || value === undefined ? '' : String(value)
  return /[";\n\r]/.test(s) ? `"${s.split('"').join('""')}"` : s
}

/**
 * One pass over the whole file, not line by line.
 *
 * A quoted field may contain a newline. A share register holds postal
 * addresses, and a street and a city on two lines of one cell is ordinary.
 * Splitting the text into lines first and parsing each one — the obvious way,
 * and the way the prototype does it — turns that single record into two broken
 * ones and shifts every field after it. The round-trip test caught exactly that:
 * two shareholders went in and three came out, the second one wearing half of
 * the first one's address as its id.
 *
 * So quotes are tracked across the whole string, and a newline ends a row only
 * when it falls outside them.
 */
function parseRows(text: string): string[][] {
  const rows: string[][] = []
  let row: string[] = []
  let cur = ''
  let quoted = false

  for (let i = 0; i < text.length; i++) {
    const c = text[i]!
    if (quoted) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          cur += '"'
          i++
        } else quoted = false
      } else cur += c
      continue
    }
    if (c === '"') quoted = true
    else if (c === SEP) {
      row.push(cur)
      cur = ''
    } else if (c === '\n' || c === '\r') {
      // CRLF is one ending, not two.
      if (c === '\r' && text[i + 1] === '\n') i++
      row.push(cur)
      cur = ''
      rows.push(row)
      row = []
    } else cur += c
  }

  row.push(cur)
  rows.push(row)
  // Files usually end with a newline, which leaves one empty row behind.
  return rows.filter((r) => r.some((cell) => cell.trim() !== ''))
}

export function parseCsv(text: string): { header: string[]; rows: string[][] } {
  // A stray BOM would otherwise become part of the first header name, so `id`
  // would not match, every row would import without one, and the result would
  // be a duplicate register rather than a replaced one.
  const clean = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text
  const rows = parseRows(clean)
  if (rows.length === 0) return { header: [], rows: [] }
  return { header: rows[0]!.map((h) => h.trim()), rows: rows.slice(1) }
}

/* -------------------------------------------------------------------------- */
/* The ledgers, packed one to a cell                                          */
/* -------------------------------------------------------------------------- */

/**
 * Nested data goes in one cell each, encoded as `a:b:c` repeated and separated
 * by commas — documented by this comment and by the header name, which is the
 * arrangement decision 56 asks for.
 *
 * ## The delimiters have to be escaped, and decision 78 is what proved it
 *
 * The bare `a:b:c` shape holds only while every field is a number, a date or an
 * id. Since 78 a purchase also carries the source's own words, and those words
 * are *"Signert tegningsblankett 14.04.2026, kurs kr 461,22"* — a list comma and
 * a decimal comma in one sentence. Packed unescaped, that single purchase comes
 * back as three, two of them with no issuance and no share count, and the
 * filter below drops them: the holding survives and the evidence for its
 * agreed price does not.
 *
 * So the two delimiters and the escape character itself are escaped on the way
 * in. After packing, every remaining `:` and `,` in a cell is a real delimiter,
 * which is what lets the split below stay a plain split.
 */
const pack = (v: unknown): string =>
  (v === null || v === undefined ? '' : String(v))
    .replace(/\\/g, '\\\\')
    .replace(/:/g, '\\c')
    .replace(/,/g, '\\p')

/** Left to right, so `\\c` unpacks to a backslash then a c, not to a colon. */
const unpack = (s: string): string =>
  s.replace(/\\(\\|c|p)/g, (_, c: string) => (c === 'c' ? ':' : c === 'p' ? ',' : '\\'))

/** The records in one packed cell, each already split into its own fields. */
const unpackRows = (cell: string): string[][] =>
  (cell || '')
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean)
    .map((x) => x.split(':').map((f) => unpack(f).trim()))

/**
 * A price on the way out, and back.
 *
 * Written with a decimal point. An empty cell is `null` rather than nought:
 * nought is a price somebody agreed, and reading a blank as one would make a
 * round look like it raised nothing from that holder. That is the same rule the
 * repository reads Firestore by, so a value cannot mean one thing in the
 * database and another in the file.
 *
 * The comma is tolerated on the way in, but it cannot rescue a comma somebody
 * typed **inside a packed cell**: there the comma is the separator between two
 * purchases, so `461,22` becomes two records before this function ever sees it,
 * and no rule can tell a decimal comma from a separator once they are the same
 * character in the same position. `checkShareholderCsv.ts` asserts that
 * behaviour rather than hiding it — a price is edited in `ShareholderModal`, not
 * in the spreadsheet. Excel does not do this on its own: the cell is text like
 * `iss-4:217:2026-04-14:461.22:…` and it leaves it alone.
 */
const packPrice = (v: number | null | undefined): string =>
  v === null || v === undefined ? '' : String(v)

const unpackPrice = (s: string): number | null => {
  const t = (s ?? '').replace(',', '.')
  return t === '' ? null : Number(t) || 0
}

/** A purchase is `issuanceId:shares:date:pricePerShare:note:paidAt`. */
const encodePurchases = (rows: SharePurchase[] = []) =>
  rows
    .map((p) =>
      [
        pack(p.issuanceId),
        pack(p.shares),
        pack(p.date),
        pack(packPrice(p.pricePerShare)),
        pack(p.note),
        // Appended, like `pricePerShare` and `note` before it, so a file
        // exported before this field existed still reads — the missing trailing
        // field simply decodes as absent.
        pack(p.paidAt),
      ].join(':'),
    )
    .join(',')

const decodePurchases = (cell: string): SharePurchase[] =>
  unpackRows(cell)
    .map((f) => {
      const note = f[4] ?? ''
      const paidAt = f[5] ?? ''
      return {
        issuanceId: f[0] ?? '',
        shares: parseInt(f[1] ?? '', 10) || 0,
        pricePerShare: unpackPrice(f[3] ?? ''),
        date: f[2] ?? '',
        // Spread in, never set to `undefined` — see the note in
        // `ownershipRepository.mapPurchases`. A present key with no value is
        // what Firestore refuses, and this one sits inside an array, where the
        // top-level strip on the way out cannot see it.
        ...(note ? { note } : {}),
        // Absent means unpaid, which is what the outstanding list reads.
        ...(paidAt ? { paidAt } : {}),
      }
    })
    .filter((p) => p.issuanceId && p.shares > 0)

/**
 * Interest in an open round — `issuanceId:shares:pricePerShare:note`.
 *
 * The point of the field is the note (decision 82: the source's own words
 * verbatim), so this is the cell the escaping above exists for.
 *
 * An empty cell decodes to an empty array rather than to nothing, because that
 * is what `mapShareholder` produces for a holder with no interest. The round
 * trip has to reproduce the record the repository would have read, not merely
 * something equivalent to it — otherwise the guard below has to know which
 * differences are allowed, and a guard with exceptions is how the thirteen
 * register fields went missing for six days.
 */
const encodeSubscriptions = (rows?: Subscription[]) =>
  (rows ?? [])
    .map((s) =>
      [pack(s.issuanceId), pack(s.shares), pack(packPrice(s.pricePerShare)), pack(s.note)].join(':'),
    )
    .join(',')

const decodeSubscriptions = (cell: string): Subscription[] =>
  unpackRows(cell)
    .map((f) => ({
      issuanceId: f[0] ?? '',
      shares: parseInt(f[1] ?? '', 10) || 0,
      pricePerShare: unpackPrice(f[2] ?? ''),
      note: f[3] ?? '',
    }))
    // The rule the repository reads by: interest with a note and no number is
    // still interest, and dropping it would lose the sentence 82 asks for.
    .filter((s) => s.issuanceId && (s.shares > 0 || s.note))

/** `count:grantDate:strike:expiry:status:exerciseDate:issuanceId:resolution:paidDate:expiredDate`. */
const encodeOptions = (rows: OptionGrant[] = []) =>
  rows
    .map((o) =>
      [
        o.count,
        o.grantDate,
        packPrice(o.strikePrice),
        o.expiryDate,
        o.status,
        o.exerciseDate,
        o.exerciseIssuanceId,
        // A board resolution is prose — "Styrevedtak 12.03.2026, sak 4" — so it
        // needs the escaping as much as a purchase note does.
        o.exerciseResolution,
        o.paidDate,
        o.expiredDate,
      ]
        .map(pack)
        .join(':'),
    )
    .join(',')

const decodeOptions = (cell: string): OptionGrant[] =>
  unpackRows(cell)
    .map((f) => ({
      count: parseInt(f[0] ?? '', 10) || 0,
      grantDate: f[1] ?? '',
      strikePrice: unpackPrice(f[2] ?? ''),
      expiryDate: f[3] ?? '',
      // A grant written before the 14 Aug round carries no status, and every
      // one of them is outstanding — see `mapOptions`.
      status: (f[4] || 'active') as OptionGrant['status'],
      exerciseDate: f[5] ?? '',
      exerciseIssuanceId: f[6] ?? '',
      exerciseResolution: f[7] ?? '',
      paidDate: f[8] ?? '',
      expiredDate: f[9] ?? '',
    }))
    .filter((o) => o.count > 0)

/**
 * The three machine-written structures, as JSON in one cell.
 *
 * Not the `a:b:c` shape, deliberately. `matches` is nested two deep and holds an
 * array inside one of its branches — it is what decision 94's `elsewhere`
 * column reads — and flattening that into positional colons would produce a
 * cell nobody could read and in which every future field shifts. JSON says what
 * it is. The split is the honest one: a ledger a person may reasonably edit in
 * Excel is positional and legible, and a structure only a script writes is
 * opaque and stays opaque.
 *
 * An unreadable cell falls back to the record rather than throwing, which is
 * why this can afford to be quiet about a parse failure: the field is restored
 * from the holder it belongs to, and the only row with nothing to restore from
 * is a row whose id is new — and a new row had none of this to begin with.
 */
const encodeJson = (v: unknown): string => (v === undefined || v === null ? '' : JSON.stringify(v))

function decodeJson<T>(cell: string): T | undefined {
  const s = (cell || '').trim()
  if (!s) return undefined
  try {
    return JSON.parse(s) as T
  } catch {
    return undefined
  }
}

/**
 * What the geocoder was given, so a stale point can be spotted.
 *
 * If any of it changed in the spreadsheet, the stored `geo` is a pin on the
 * previous address — and so is the one in the file, which was exported before
 * the edit. Dropping it is the only correct answer: `geocodeAll.ts` places the
 * record again on its next run, and a record with no point is named in the
 * map's tray with its reason (87), which is visible. A pin on the old house is
 * not.
 */
const placeKey = (s: {
  address?: string
  postalCode?: string
  city?: string
  country?: string
}) =>
  [s.address ?? '', s.postalCode ?? '', s.city ?? '', s.country ?? '']
    .join('|')
    .trim()
    .toLowerCase()

/** `matches` is a join on the email address and nothing else — see 94. */
const matchKey = (s: { email?: string; altEmail?: string }) =>
  [s.email ?? '', s.altEmail ?? ''].join('|').trim().toLowerCase()

/* -------------------------------------------------------------------------- */

export function toCsv(holders: Shareholder[]): string {
  const rows = holders.map((s) =>
    [
      s.id,
      s.name,
      s.legalName,
      s.kind,
      s.orgNumber,
      s.personnummer,
      s.birthNote,
      s.contactPerson,
      s.email,
      s.altEmail,
      s.phone,
      s.address,
      s.postalCode,
      s.city,
      s.country,
      s.entryDate,
      s.shareSeries,
      s.vpsAccount,
      s.folkeinvestRef,
      s.nameVariants,
      s.sources,
      s.linkedLeadId,
      s.linkedLeadContactId,
      s.linkedLeadContact,
      s.linkedAmbassadorId,
      s.linkedTeamEmail,
      s.linkedLabel,
      encodePurchases(s.purchases),
      encodeSubscriptions(s.subscriptions),
      encodeOptions(s.options),
      encodeJson(s.geo),
      encodeJson(s.matches),
      encodeJson(s.attachments),
      s.createdAt,
      s.updatedAt,
    ]
      .map(escape)
      .join(SEP),
  )
  return [CSV_COLUMNS.join(SEP), ...rows].join('\n')
}

export interface ImportPlan {
  fileName: string
  rowCount: number
  currentCount: number
  /** Columns the register holds that the file does not, so they will be cleared. */
  missing: string[]
  /** Names on the register that the file has no row for. */
  dropped: string[]
  /** Of those, the ones carrying files — their attachments go with them. */
  droppedWithFiles: string[]
  /** Non-empty means the file is refused and nothing was read. */
  error: string
  rows: Shareholder[]
}

/**
 * Read a file and say what applying it would do — nothing is written here.
 *
 * Four refusals, each of which would otherwise destroy the register quietly.
 * The first two are decision 56's: a file with no `name` column is not an
 * export of this thing at all, and a file with a header and no rows would empty
 * it. The last two are decision 96's, adopted from the build after 17 Aug 2026,
 * when a contact list carrying a name column replaced the whole register — 145
 * rows in, two real holdings out.
 */
export function planImport(fileName: string, text: string, current: Shareholder[]): ImportPlan {
  const { header, rows } = parseCsv(text)
  const at = (name: string) => header.indexOf(name)
  const cell = (row: string[], name: string) => {
    const i = at(name)
    return i === -1 ? '' : (row[i] ?? '').trim()
  }
  /** An optional field is absent when its cell is empty, never `''`. */
  const opt = (row: string[], name: string) => cell(row, name) || undefined

  const byId = new Map(current.map((s) => [s.id, s]))
  const incomingIds = new Set(
    at('id') === -1 ? [] : rows.map((r) => (r[at('id')] ?? '').trim()).filter(Boolean),
  )
  /**
   * Does this file know anybody already on the register?
   *
   * The round trip this feature exists for is export → edit → import, and an
   * export always carries ids, so a genuine re-import always recognises at
   * least one holder. A file that recognises none is not a corrected copy of
   * this register — it is a different list that happens to have names in it.
   */
  const recognised = [...incomingIds].some((id) => byId.has(id))

  const error =
    at('name') === -1
      ? 'That file has no “name” column, so it is not an export of this register. Nothing was read.'
      : rows.length === 0
        ? 'That file has a header but no rows. Importing it would empty the register, so it is refused.'
        : at('id') === -1 && current.length > 0
          ? 'That file has no “id” column. An export of this register always carries one, so this is not a corrected copy of it — importing it would replace every shareholder with a new record. Nothing was read.'
          : current.length > 0 && !recognised
            ? `That file does not contain a single one of the ${current.length} shareholders on the register, so it is not an export of it. Importing it would delete all of them and add ${rows.length} unrelated rows. Nothing was read.`
            : ''
  const dropped = current.filter((s) => !incomingIds.has(s.id))

  const parsed: Shareholder[] = error
    ? []
    : rows.map((row) => {
        const id =
          cell(row, 'id') || `sh-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`
        const prev = byId.get(id)
        const holder: Shareholder = {
          id,
          name: cell(row, 'name'),
          kind: cell(row, 'kind') === 'company' ? 'company' : 'person',
          orgNumber: cell(row, 'orgNumber'),
          personnummer: cell(row, 'personnummer'),
          email: cell(row, 'email'),
          phone: cell(row, 'phone'),
          address: cell(row, 'address'),
          linkedLeadId: cell(row, 'linkedLeadId'),
          linkedLeadContactId: cell(row, 'linkedLeadContactId'),
          linkedLeadContact: cell(row, 'linkedLeadContact'),
          linkedAmbassadorId: cell(row, 'linkedAmbassadorId'),
          linkedTeamEmail: cell(row, 'linkedTeamEmail'),
          linkedLabel: cell(row, 'linkedLabel'),
          purchases: decodePurchases(cell(row, 'purchases')),
          options: decodeOptions(cell(row, 'options')),
          // ---- The aksjeeierbok's own thirteen, every one of them optional ----
          legalName: opt(row, 'legalName'),
          contactPerson: opt(row, 'contactPerson'),
          altEmail: opt(row, 'altEmail'),
          postalCode: opt(row, 'postalCode'),
          city: opt(row, 'city'),
          country: opt(row, 'country'),
          birthNote: opt(row, 'birthNote'),
          shareSeries: opt(row, 'shareSeries'),
          entryDate: opt(row, 'entryDate'),
          vpsAccount: opt(row, 'vpsAccount'),
          folkeinvestRef: opt(row, 'folkeinvestRef'),
          nameVariants: opt(row, 'nameVariants'),
          sources: opt(row, 'sources'),
        }

        holder.subscriptions = decodeSubscriptions(cell(row, 'subscriptions'))

        /**
         * The record wins over the file for anything a script wrote.
         *
         * Attachments cannot travel through a spreadsheet — the bytes are in
         * Storage — so they are taken from the record this row matches by id,
         * which is decision 56 and the whole reason 54 had to be built first.
         * `geo` and `matches` follow the same rule for the same reason: they are
         * answers a script computed, and a cell somebody nudged in Excel must
         * not be able to move a pin or claim a lead. The file is the fallback,
         * so a register restored onto an empty collection still comes back
         * whole.
         */
        const attachments = prev?.attachments ?? decodeJson<Attachment[]>(cell(row, 'attachments'))
        holder.attachments = attachments ?? ([] as Attachment[])

        const geo = prev?.geo ?? decodeJson<GeoPoint>(cell(row, 'geo'))
        // Kept only while it still answers the address on the row — see `placeKey`.
        if (geo && (!prev || placeKey(prev) === placeKey(holder))) holder.geo = geo

        const matches = prev?.matches ?? decodeJson<ShareholderMatches>(cell(row, 'matches'))
        if (matches && (!prev || matchKey(prev) === matchKey(holder))) holder.matches = matches

        // The day a holder came onto the register is a fact about them, not
        // about this write, so it is carried through — `replaceRegister` writes
        // it back rather than letting a `set` without merge drop it.
        const createdAt = cell(row, 'createdAt') || prev?.createdAt
        if (createdAt) holder.createdAt = createdAt
        const updatedAt = cell(row, 'updatedAt') || prev?.updatedAt
        if (updatedAt) holder.updatedAt = updatedAt

        return holder
      })

  return {
    fileName,
    rowCount: rows.length,
    currentCount: current.length,
    missing: CSV_COLUMNS.filter((c) => at(c) === -1 && !MACHINE_WRITTEN.includes(c)),
    dropped: dropped.map((s) => s.name),
    droppedWithFiles: dropped.filter((s) => (s.attachments?.length ?? 0) > 0).map((s) => s.name),
    error,
    rows: parsed,
  }
}
